import { blobToFile, fileToImg } from "./convert";

/**
 * @typedef {{x: number, x2: number, y: number, y2: number, width: number, height: number, imageWidth: number, imageHeight: number}} InnerRect
 * @typedef {{x: number, y: number, width: number, height: number}} Rect
 */

export function getCanvas(width, height) {
	let canvas
	// OffscreenCanvas有些问题, 后续处理
	// try {
	// 	canvas = new OffscreenCanvas(width, height)
	// } catch (error) {
		canvas = document.createElement('canvas')
		canvas.width = width
		canvas.height = height
	// }
	return canvas
}


/**
 * 创建image对象
 * @param {string} url
 * @returns {Promise<HTMLImageElement>} 
 */
export async function createImage(url) {
	const img = await new Promise((resolve, reject) => {
		const img = new Image()
		img.onload = () => {
			resolve(img)
		}
		img.onerror = (err) => {
			reject(err)
		}
		img.setAttribute('crossOrigin', 'anonymous')

		if(!url.includes('ycsd.work')){
			url = 'https://api.ycsd.work/api/collocation/image/proxy?url=' + encodeURIComponent(url)
		}

		img.src = url
	})
	return img
}

/** 是否存在透明像素 */
function isExistTransparentPx(data) {
	// 第三个位置是透明度, 小于255的表示一定程度的透明, 0为完全透明
	return data < 255
}

/**
 * 给定图片是否存在透明区域
 * @param {string} url
 */
export async function isImageExistTransparentByUrl(url) {
	const img = await createImage(url)
	return isImageExistTransparent(img)
}
/**
 * 给定图片是否存在透明区域
 * @param {HTMLImageElement} img
 */
export function isImageExistTransparent(img) {
	let canvas = getCanvas(img.width, img.height)
	canvas.getContext('2d').drawImage(img, 0, 0, img.width, img.height);
	// 只检测1像素
	let imageData = canvas.getContext('2d').getImageData(0, 0, 1, 1);
	if (isExistTransparentPx(imageData.data[3])) {
			return true
	} else {
			return false
	}
}
/**
 * 获取透明图内最小物体的矩形, 原图片左上角为原点
 * @param {string} url
 * @param {((v: number) => number)?} convertXFunc 转换函数
 * @param {((v: number) => number)?} convertYFunc 转换函数
 * @returns {Promise<InnerRect>}
 */
export async function getTransparentImageInnerRectByUrl(url, convertXFunc, convertYFunc) {
	const img = await createImage(url)
	return getTransparentImageInnerRect(img, convertXFunc, convertYFunc)
}

/**
 * 获取透明图内最小物体的矩形, 原图片左上角为原点
 * 如果输入的不是一个透明图, 则返回原图数据
 * @param {HTMLImageElement} img
 * @param {((v: number) => number)?} convertXFunc 转换函数
 * @param {((v: number) => number)?} convertYFunc 转换函数
 * @returns {InnerRect}
 */
export function getTransparentImageInnerRect(img, convertXFunc, convertYFunc) {
    const rect = {
        x: 0, x2: 0, y: 0, y2: 0,
        width: 0, height: 0,
        imageWidth: img.width, imageHeight: img.height,
    }

    try {
        const canvas = getCanvas(img.width, img.height)
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, img.width, img.height);

        // 获取所有像素数据
        const imageData = ctx.getImageData(0, 0, img.width, img.height).data;

        // 初始化边界值：反向设置，以便在循环中缩小范围
        let minX = img.width, minY = img.height, maxX = 0, maxY = 0;
        let foundPixel = false;

        // 遍历所有像素 (r, g, b, a) -> 每4个一组
        for (let y = 0; y < img.height; y++) {
            for (let x = 0; x < img.width; x++) {
                const alphaIndex = (y * img.width + x) * 4 + 3;
                const alpha = imageData[alphaIndex];

                // 只要透明度大于0（即使是半透明），就算作有效内容
                if (alpha > 0) {
                    if (x < minX) minX = x;
                    if (x > maxX) maxX = x;
                    if (y < minY) minY = y;
                    if (y > maxY) maxY = y;
                    foundPixel = true;
                }
            }
        }

        if (foundPixel) {
            // 修正maxX和maxY，因为坐标是0-index，宽高需要+1
            // 例如 x=5 是像素点，width 应该是 1 (5-5+1不行，逻辑上 rect.x2 通常指右边界坐标)
            // 按照你之前的逻辑: width = x2 - x
            // 如果 x=5 是最右边的点，那么 x2 应该是 6 才能算出 width=1
            rect.x = minX;
            rect.x2 = maxX + 1;
            rect.y = minY;
            rect.y2 = maxY + 1;
            rect.width = rect.x2 - rect.x;
            rect.height = rect.y2 - rect.y;
        } else {
            // 全是透明像素（空图），或者 getImageData 读取失败
            // 保持 width/height 为 0，或者根据需求重置为原图
            console.warn('Image is fully transparent or empty');
            rect.x = 0;
            rect.y = 0;
            rect.width = img.width;
            rect.height = img.height;
            rect.x2 = img.width;
            rect.y2 = img.height;
        }

    } catch (e) {
        console.error("计算透明矩形失败(可能是跨域问题):", e);
        // 降级处理：返回原图大小
        rect.x = 0; rect.x2 = img.width;
        rect.y = 0; rect.y2 = img.height;
        rect.width = img.width; rect.height = img.height;
    }

    // 执行转换函数（保持原有逻辑）
    if (convertXFunc && !convertYFunc) {
        convertYFunc = convertXFunc
    }
    if (convertXFunc && convertYFunc) {
        rect.x = convertXFunc(rect.x)
        rect.x2 = convertXFunc(rect.x2)
        rect.y = convertYFunc(rect.y)
        rect.y2 = convertYFunc(rect.y2)
        rect.width = convertXFunc(rect.width)
        rect.height = convertYFunc(rect.height)
        rect.imageWidth = convertXFunc(rect.imageWidth)
        rect.imageHeight = convertYFunc(rect.imageHeight)
    }

    return rect
}

/**
 * 获取透明图内部图片距周围空白大小
 * @param {{width: number, height: number}} originRect 透明图大小
 * @param {InnerRect} innerRect 
 * @returns {{left: number, right: number, top: number, bottom: number}}
 */
export function getInnerRectBlankSize(originRect, innerRect) {
	return {
    left: originRect.width * (innerRect.x / innerRect.imageWidth),
    right: originRect.width * ((innerRect.imageWidth - innerRect.x2) / innerRect.imageWidth),
    top: originRect.height * (innerRect.y / innerRect.imageHeight),
    bottom: originRect.height * ((innerRect.imageHeight - innerRect.y2) / innerRect.imageHeight),
  }
}
/**
 * 保持其宽高比地展示元素最短的边
 * @param {{width: number, height: number}} containerSize 
 * @param {{width: number, height: number}} elementSize 
 * @returns {{width: number, height: number}}
 */
export function cover(containerSize, elementSize) {
  const containerRatio = containerSize.width / containerSize.height
  const elementRatio = elementSize.width / elementSize.height

  let width, height

  if (containerRatio > elementRatio) {
    width = containerSize.width
    height = containerSize.width / elementRatio
  } else {
    width = containerSize.height * elementRatio
    height = containerSize.height
  }

  return { width, height }
}
/**
 * 保持其宽高比地完全展示元素
 * @param {{width: number, height: number}} containerSize 
 * @param {{width: number, height: number}} elementSize 
 * @returns {{width: number, height: number}}
 */
export function contain(containerSize, elementSize) {
	const containerRatio = containerSize.width / containerSize.height
  const elementRatio = elementSize.width / elementSize.height

  let width, height

  if (containerRatio < elementRatio) {
    width = containerSize.width
    height = containerSize.width / elementRatio
  } else {
    width = containerSize.height * elementRatio
    height = containerSize.height
  }
  return { width, height }
}
/**
 * 计算实际长高, 会填充空白内容
 * @param {number} originWidth 
 * @param {number} originHeight 
 * @param {number} targetWidth 
 * @param {number} targetHeight 
 * @returns {{realWidth: number, realHeight: number}}
 */
export function computedRealWidthHeight(originWidth, originHeight, targetWidth, targetHeight) {
	const {width, height} = contain({
		width: targetWidth,
		height: targetHeight,
	}, {
		width: originWidth,
		height: originHeight,
	})
	return {
		realWidth: width,
		realHeight: height,
	}
}
/**
 * 通过最大宽高等比例缩放图片像素
 * @param {File} file 
 * @param {number} maxWidth 
 * @param {number} maxHeight 
 * @returns {Promise<File>}
*/
export async function scaleImageByMaxWidthHeight(file, maxWidth, maxHeight) {
	if (!(maxWidth && maxHeight)) {
		throw new Error("最大缩放长宽无效")
	} 
	const img = await fileToImg(file)
	let cWidth
	let cHeight
	if (img.width < maxWidth && img.height < maxHeight) {
		cWidth = img.width
		cHeight = img.height
	} else {
		if (img.width < img.height) {
			cWidth = img.width / img.height * maxHeight
			cHeight = maxHeight
		} else {
			cWidth = maxWidth
			cHeight = img.height / img.width * maxWidth
		}
	}
	const canvasNode = getCanvas(cWidth, cHeight)
	const ctx = canvasNode.getContext("2d")
	ctx.drawImage(img, 0, 0, img.width, img.height, 0, 0, cWidth, cHeight)
	const blob = await new Promise((resolve) => {
		canvasNode.toBlob((blob) => resolve(blob), file.type)
	})
  const newFile = blobToFile(blob, file.name)
	return newFile
}